import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import {
  BackendChannelModel,
  ChannelDataResponse,
  ChannelFormData,
} from "types/channels";
import { ListingProps, ListResponse } from "types/listing";
import { getEnvVariable } from "utilities/env";

export const useChannels = (channelID?: string | string[]) => {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher, arrayFetcher } = useAuth();

  const useChannelList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<BackendChannelModel>> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/channel${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteChannelsList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<BackendChannelModel>>(
      (index) =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/channel${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const addChannel = async (values: ChannelFormData) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/channel`,
      {
        body: JSON.stringify({ channel: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editChannel = async (values: ChannelFormData) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/channel/${channelID}`,
      {
        body: JSON.stringify({ channel: values }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const useChannelsData = <T extends string | string[]>(
    channelID?: T,
  ): SWRResponse<ChannelDataResponse<T>> => {
    const isSingleRequest = channelID && typeof channelID === "string";

    return useSWR(
      () =>
        code && channelID
          ? isSingleRequest
            ? `${getEnvVariable(
                "REACT_APP_API_URL",
              )}/${code}/channel/${channelID}`
            : (channelID as []).map(
                (id) =>
                  `${getEnvVariable(
                    "REACT_APP_API_URL",
                  )}/${code}/channel/${id}`,
              )
          : null,
      isSingleRequest ? fetcher : arrayFetcher,
    );
  };

  const removeChannel = async (channel: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/channel/${channel}`,
      { method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  return {
    addChannel,
    editChannel,
    removeChannel,
    useChannelList,
    useChannelsData,
    useInfiniteChannelsList,
  };
};
